<template>
  <div class="box-tagging" @keydown="onKeydown" tabindex="0" ref="root">
    <!-- Scope the queue — the same shared filter the group-mode views use.
         Response Group ID / response-id search, retailer, project, job, date
         range, department. [IT-744] -->
    <div class="box-tagging__filter">
      <PhotoFilter :initial-filter="groupFilters" @apply-filters="setFilters" />
      <v-checkbox
        v-if="reviewMode"
        v-model="onlyNeedsReview"
        dense hide-details label="Only needs review"
        class="box-tagging__needs-review"
        @change="reloadQcQueue"
      />
      <v-checkbox
        v-if="reviewMode"
        v-model="showQCed"
        dense hide-details label="Show groups already QCed"
        class="box-tagging__needs-review"
        @change="reloadQcQueue"
      />
    </div>

    <!-- Header: what I'm working on, and how much is left -->
    <div class="box-tagging__bar">
      <div class="box-tagging__ident">
        <v-chip v-if="reviewMode" color="primary" x-small label class="mr-2">QC</v-chip>
        <span v-if="group">
          Group {{ group.response_group_id }}
          <span class="text--secondary">· Q{{ group.question_id }}</span>
          <!-- IT-843: surface who tagged the group on the Box-QC tab so QC
               reviewers (Cloud Factory) have it for easy reference. -->
          <span v-if="reviewMode && group.tagger" class="text--secondary">· Tagged by {{ group.tagger }}</span>
        </span>
        <span v-else class="text--secondary">{{ reviewMode ? 'No groups to review' : 'No group loaded' }}</span>
      </div>

      <div class="box-tagging__stats">
        <span class="stat">
          <strong>{{ totalActiveCount }}</strong> boxes
        </span>
        <span class="stat" :class="{'stat--done': untaggedCount === 0 && totalActiveCount > 0}">
          <strong>{{ untaggedCount }}</strong> untagged
        </span>
      </div>

      <div class="box-tagging__controls">
        <v-btn-toggle :value="viewMode" dense mandatory @change="setViewMode">
          <v-btn small value="focus" title="Focus one photo (G)">
            <v-icon small>mdi-image</v-icon>
          </v-btn>
          <v-btn small value="group" title="Whole shelf (G)">
            <v-icon small>mdi-view-grid</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-btn-toggle :value="interactionMode" dense mandatory class="ml-2" @change="setInteractionMode">
          <v-btn small value="select" title="Select boxes (Z)">
            <v-icon small>mdi-cursor-default-outline</v-icon>
          </v-btn>
          <v-btn small value="zoom" title="Zoom / pan (Z)">
            <v-icon small>mdi-magnify</v-icon>
          </v-btn>
          <v-btn small value="draw" title="Draw / edit boxes (D)">
            <v-icon small>mdi-vector-rectangle</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-btn
          v-if="interactionMode === 'draw'"
          small text
          :disabled="!selectedBoxIds.length"
          title="Delete selected box"
          @click="deleteSelected"
        >
          <v-icon small left>mdi-trash-can-outline</v-icon>
          Delete
        </v-btn>

        <v-btn small text title="Keyboard shortcuts (?)" @click="showHelp = !showHelp">
          <v-icon small>mdi-keyboard-outline</v-icon>
        </v-btn>

        <!-- QC review: approve / flag the current group and advance. [IT-745] -->
        <template v-if="reviewMode && group">
          <v-btn
            small color="success" class="ml-2"
            :disabled="isSaving" :loading="isSaving"
            @click="approveGroup"
          >
            <v-icon small left>mdi-check</v-icon>Approve &amp; Next
          </v-btn>
          <v-btn
            small color="warning" outlined class="ml-1"
            :disabled="isSaving"
            @click="flagGroup"
          >
            <v-icon small left>mdi-flag-outline</v-icon>Flag for rework
          </v-btn>
        </template>

        <!-- Finish (tagger): mirror the other modes' submit batch, gated on the
             server's own "done" rule (every active box tagged). -->
        <template v-else-if="group">
          <span
            v-if="!groupSubmittable"
            class="submit-hint"
            :title="submitBlockedCount + ' box(es) still untagged'"
          >{{ submitBlockedCount }} untagged</span>
          <v-btn
            small color="success" class="ml-2"
            :disabled="!groupSubmittable || isSaving"
            :loading="isSaving"
            @click="openSubmit(true)"
          >Submit &amp; Get Next Group</v-btn>
          <v-btn
            small outlined class="ml-1"
            :disabled="!groupSubmittable || isSaving"
            @click="openSubmit(false)"
          >Submit &amp; Close</v-btn>
        </template>
      </div>
    </div>

    <div v-if="isLoading" class="box-tagging__loading">
      <v-progress-circular indeterminate />
    </div>

    <div v-else-if="!group" class="box-tagging__empty">
      <h3>{{ reviewMode ? 'No groups to review' : 'No groups available for tagging' }}</h3>
      <v-btn color="primary" class="mt-4" @click="loadGroup">
        {{ reviewMode ? 'Check for groups to review' : 'Check for new groups' }}
      </v-btn>
    </div>

    <template v-else>
      <!-- Filmstrip: which photo am I on, and where is the remaining work -->
      <div v-if="viewMode === 'focus'" class="box-tagging__filmstrip">
        <button
          v-for="(photo, index) in group.photos"
          :key="photo.id"
          class="thumb"
          :class="{'is-active': photo.id === activePhotoId}"
          @click="selectPhoto(photo.id)"
        >
          <img
            :src="thumbUrl(photo.image_url)"
            class="thumb__img"
            loading="lazy"
            @error="onThumbError($event, photo)"
          />
          <span class="thumb__index">{{ index + 1 }}</span>
          <span
            v-if="untaggedOnPhoto(photo.id)"
            class="thumb__badge"
          >{{ untaggedOnPhoto(photo.id) }}</span>
          <v-icon v-else small class="thumb__done">mdi-check-circle</v-icon>
        </button>
      </div>

      <div class="box-tagging__body">
        <div class="box-tagging__stage">
          <!-- Focus: one photo, big -->
          <BoxCanvas
            v-if="viewMode === 'focus' && activePhoto"
            :photo="activePhoto"
            :boxes="activePhotoBoxes"
            :selected-ids="selectedBoxIds"
            :hide-tagged="hideTagged"
            :hide-invalid="hideInvalid"
            :tag-names="tagNames"
            :category-tag-ids="activeCategoryTagIds"
            :duplicate-tag-id="duplicateTagId"
            :hide-duplicate="hideDuplicate"
            :mode="interactionMode"
            @draw-box="onDrawBox"
            @resize-box="onResizeBox"
            @replace-select="onReplaceSelect"
            @toggle-select="onToggleSelect"
            @clear-select="clearSelection"
            @marquee-replace="onMarqueeReplace"
            @marquee-add="onMarqueeAdd"
            @marquee-subtract="onMarqueeSubtract"
          />

          <!--
            Group: every photo of the section side by side. Selection spans
            them, because the shelf does — a marquee dragged from one frame
            into the next picks up boxes in both.
          -->
          <div v-else class="box-tagging__grid">
            <div v-for="(photo, index) in group.photos" :key="photo.id" class="box-tagging__grid-item">
              <div class="box-tagging__grid-label">Photo {{ index + 1 }}</div>
              <div class="box-canvas-host">
              <BoxCanvas
                :photo="photo"
                :boxes="boxesForPhoto(photo.id)"
                :selected-ids="selectedBoxIds"
                :hide-tagged="hideTagged"
                :hide-invalid="hideInvalid"
                :tag-names="tagNames"
                :category-tag-ids="activeCategoryTagIds"
                :duplicate-tag-id="duplicateTagId"
                :hide-duplicate="hideDuplicate"
                :mode="interactionMode"
                @draw-box="onDrawBox"
                @resize-box="onResizeBox"
                :max-height-vh="0.78"
                fit-height
                @replace-select="onReplaceSelect"
                @toggle-select="onToggleSelect"
                @clear-select="clearSelection"
                @marquee-replace="onMarqueeAdd"
                @marquee-add="onMarqueeAdd"
                @marquee-subtract="onMarqueeSubtract"
              />
              </div>
            </div>
          </div>

          <div class="box-tagging__legend">
            <span class="legend-item"><i class="swatch swatch--untagged"></i> Untagged</span>
            <span class="legend-item"><i class="swatch swatch--untagged-selected"></i> Untagged + selected</span>
            <span class="legend-item"><i class="swatch swatch--tagged"></i> Tagged</span>
            <span class="legend-item"><i class="swatch swatch--tagged-selected"></i> Tagged + selected</span>
            <span class="legend-item"><i class="swatch swatch--invalid"></i> Invalid</span>
            <span class="legend-item"><i class="swatch swatch--duplicate"></i> Duplicate</span>

            <v-spacer />

            <span class="show-label">Show</span>
            <v-btn-toggle v-model="visibleKinds" multiple dense class="legend-show">
              <v-btn small value="tagged">Tagged</v-btn>
              <v-btn small value="invalid">Invalid</v-btn>
              <v-btn small value="duplicate">Duplicate</v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <aside class="box-tagging__side">
          <ShareOfShelfPanel
            :shares="shareOfShelf"
            :untagged-share="untaggedShare"
            :tag-names="tagNames"
          />

          <v-divider class="my-3" />

          <BoxTagPanel
            ref="tagPanel"
            :categories="tagCategories"
            :active-category-id="activeCategoryId"
            :reject-reason="rejectReason"
            :selected-count="selectedBoxIds.length"
            :selected-tagged-count="selectedTaggedCount"
            :pending-tags="pendingTags"
            :tag-state="tagStateForSelection"
            :is-saving="isSaving"
            :hierarchy-facets="hierarchyFacets"
            :selected-hierarchy-ids="selectedHierarchyIds"
            :ancestor-map="ancestorMap"
            @cycle-tag="onCycleTag"
            @set-category="onSetCategory"
            @set-reject-reason="rejectReason = $event"
            @set-hierarchy-filter="selectedHierarchyIds = $event"
            @apply="applyTags"
            @reject="reject"
            @clear-selection="clearSelection"
          />
        </aside>
      </div>
    </template>

    <v-dialog v-model="showHelp" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">Keyboard shortcuts</v-card-title>
        <v-card-text>
          <div v-for="item in shortcuts" :key="item.keys" class="shortcut">
            <kbd>{{ item.keys }}</kbd><span>{{ item.label }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn text @click="showHelp = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="submitDialog.show" max-width="440">
      <v-card>
        <v-card-title class="subtitle-1">Submit this group?</v-card-title>
        <v-card-text>
          <p class="mb-2">
            {{ totalActiveCount }} box<span v-if="totalActiveCount !== 1">es</span>, all tagged.
            Submitting marks the group complete, releases it for review, and
            unassigns it from you.
          </p>
          <p v-if="hasPendingChanges" class="warning--text mb-0">
            You have unsaved tag changes that haven't been applied — they will be
            discarded. Cancel and press Apply first if you want to keep them.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="isSaving" @click="submitDialog.show = false">Cancel</v-btn>
          <v-btn color="success" :loading="isSaving" @click="confirmSubmit">
            {{ submitDialog.getNext ? 'Submit & Get Next' : 'Submit & Close' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="5000">
      {{ snackbar.text }}
      <template v-slot:action="{attrs}">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Dismiss</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex'
import BoxCanvas from '@/components/boxes/BoxCanvas.vue'
import BoxTagPanel from '@/components/boxes/BoxTagPanel.vue'
import ShareOfShelfPanel from '@/components/boxes/ShareOfShelfPanel.vue'
import PhotoFilter from '@/components/filters/PhotoFilter'
import {routeFilterSeed} from '@/components/filters/routeSeed'

export default {
  name: 'BoxTagging',

  components: {BoxCanvas, BoxTagPanel, ShareOfShelfPanel, PhotoFilter},

  // reviewMode (set by the /box-qc route) runs the same canvas as a QC surface:
  // the queue comes from quality_control, box edits write with isQc, and the
  // submit bar becomes Approve / Flag instead of the tagger's Submit. [IT-745]
  props: {
    reviewMode: {type: Boolean, default: false},
  },

  data() {
    return {
      // Box-QC: "only needs review" queue toggle. [IT-745]
      onlyNeedsReview: false,
      // Box-QC: include already-reviewed groups in the queue so a reviewer can
      // pull a QCd group (e.g. a specific RGID) back up to fix outliers. [IT-788]
      showQCed: false,
      // Shared PhotoFilter payload scoping the queue. Mirrors Group Tagging's
      // shape (unused keys kept for shape-compat with the component). [IT-744]
      groupFilters: {
        fromDt: null,
        thruDt: null,
        tags: [],
        projects: [],
        jobs: [],
        onlyUntagged: true,
        includePartiallyTagged: true,
        includeInactive: false,
        respIdSearchFreeForm: null,
        rgidSearchFreeForm: null,
        country: null,
        retailer: null,
        department: null,
      },
      // show toggles — a kind absent here is hidden on the canvas.
      visibleKinds: ['tagged', 'invalid', 'duplicate'],
      rejectReason: 'invalid',  // 'invalid' | 'duplicate' — sticky across rejects
      // "Narrow by" hierarchical ancestor filter: tag ids of selected facet
      // values; narrows the active category's pick list (view-only). Cleared on
      // category switch since facets differ per category.
      selectedHierarchyIds: [],
      interactionMode: 'select',  // 'select' | 'zoom' (IT-721)
      submitDialog: {show: false, getNext: true},
      showHelp: false,
      snackbar: {show: false, text: ''},
      shortcuts: [
        {keys: 'drag', label: 'Marquee-select (shift adds, alt / ⌥ option subtracts)'},
        {keys: 'click', label: 'Select one (⌘/ctrl toggles)'},
        {keys: 'Enter', label: 'Apply pending tags'},
        {keys: 'A', label: 'Select all boxes'},
        {keys: 'U', label: 'Select untagged boxes'},
        {keys: 'I', label: 'Invert selection'},
        {keys: 'Esc', label: 'Clear selection'},
        {keys: 'Del', label: 'Reject selection (invalid / duplicate per toggle)'},
        {keys: '← →', label: 'Previous / next photo'},
        {keys: 'G', label: 'Toggle focus / whole-shelf view'},
        {keys: 'Z', label: 'Toggle zoom / select'},
        {keys: 'D', label: 'Toggle draw / select (Del removes the box)'},
        {keys: '/', label: 'Jump to the tag filter'},
        {keys: '?', label: 'This dialog'},
      ],
    }
  },

  computed: {
    ...mapState({
      group: state => state.boxes.group,
      activePhotoId: state => state.boxes.activePhotoId,
      activeCategoryId: state => state.boxes.activeCategoryId,
      selectedBoxIds: state => state.boxes.selectedBoxIds,
      pendingTags: state => state.boxes.pendingTags,
      viewMode: state => state.boxes.viewMode,
      isLoading: state => state.boxes.isLoading,
      isSaving: state => state.boxes.isSaving,
      allTags: state => state.tags.tags || [],
      allCategories: state => state.categories.categories || [],
      ancestorMap: state => state.tags.ancestorMap || {},
    }),

    ...mapGetters([
      'allBoxes', 'activePhoto', 'activePhotoBoxes', 'selectedBoxes',
      'untaggedCount', 'totalActiveCount', 'untaggedOnPhoto',
      'shareOfShelf', 'untaggedShare', 'tagStateForSelection',
      'pendingAddIds', 'pendingRemoveIds', 'activeCategoryTagIds', 'rejectionTagIds',
      'groupSubmittable', 'submitBlockedCount', 'hasPendingChanges',
      // IT-704 (P4): hide machine-owned (derived) categories from the box tag
      // panel once derivation is enabled. The panel applies tag deltas
      // (add_tag_ids / remove_tag_ids), never a full set, so hiding here cannot
      // wipe a box's derived tags — it just stops a tagger hand-picking them.
      'visibleCategories', 'visibleTags',
    ]),

    selectedTaggedCount() {
      // Count 'already tagged' the same way the canvas does — scoped to the
      // active category so the panel summary agrees with the boxes. [IT-719]
      const ids = this.activeCategoryTagIds
      const tagged = b => ids == null ? !!b.tagged : (b.tag_ids || []).some(id => ids.includes(id))
      return this.selectedBoxes.filter(tagged).length
    },

    hideTagged() { return !this.visibleKinds.includes('tagged') },
    hideInvalid() { return !this.visibleKinds.includes('invalid') },
    hideDuplicate() { return !this.visibleKinds.includes('duplicate') },
    duplicateTagId() { return this.rejectionTagIds.duplicate },

    tagNames() {
      const map = {}
      this.allTags.forEach(tag => { map[tag.id] = tag.name })
      return map
    },

    /**
     * The tag panel wants [{id, name, tags: [...]}], but the API serves
     * categories and tags as two flat lists with the category hanging off each
     * tag. Join them here rather than teaching the panel about the API shape.
     *
     * Tags whose category is null are real (the catalog allows it), so they get
     * an "Uncategorised" bucket instead of silently disappearing from the only
     * UI that can apply them.
     */
    tagCategories() {
      const byId = new Map()
      // Exclude trigger_delete (Rejection) categories — their reason tags are
      // applied via the dedicated reject toggle/button, never as ordinary tags,
      // so they must not appear in the category selector or tag list. [IT-726]
      this.visibleCategories
        .filter(c => !c.trigger_delete)
        .forEach(c => byId.set(c.id, {id: c.id, name: c.name, tags: []}))

      const loose = {id: null, name: 'Uncategorised', tags: []}
      this.visibleTags.forEach(tag => {
        if (tag.category && tag.category.trigger_delete) return  // reject reason tag — not selectable here
        const categoryId = tag.category && tag.category.id
        const bucket = byId.get(categoryId) || loose
        bucket.tags.push({id: tag.id, name: tag.name})
      })

      const groups = Array.from(byId.values()).filter(c => c.tags.length)
      if (loose.tags.length) groups.push(loose)
      return groups
    },

    tagById() {
      const map = {}
      this.allTags.forEach(tag => { map[tag.id] = tag })
      return map
    },

    /**
     * "Narrow by" facets for the active category: the hierarchical categories
     * reachable via the ancestors of this category's tags, each with its
     * ancestor tags. E.g. Sub-Brand rolls up to Brand + Category, so a tagger
     * can keep only Paper-Towel sub-brands. Empty unless a human category is
     * active and its tags have hierarchical ancestors.
     */
    hierarchyFacets() {
      if (this.activeCategoryId == null) return []
      const activeTags = this.visibleTags.filter(
        t => t.category && t.category.id === this.activeCategoryId,
      )
      const ancestorIds = new Set()
      activeTags.forEach(t => (this.ancestorMap[t.id] || []).forEach(id => ancestorIds.add(id)))
      if (!ancestorIds.size) return []

      const byCat = new Map()
      ancestorIds.forEach(id => {
        const tag = this.tagById[id]
        if (!tag || !tag.category || !tag.category.is_hierarchical) return
        if (!byCat.has(tag.category.id)) {
          byCat.set(tag.category.id, {category: {id: tag.category.id, name: tag.category.name}, tags: []})
        }
        byCat.get(tag.category.id).tags.push({id: tag.id, name: tag.name})
      })

      return Array.from(byCat.values())
        .map(facet => ({
          ...facet,
          tags: facet.tags.slice().sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.category.name.localeCompare(b.category.name))
    },
  },

  created() {
    // Quick-link deep links (routeSeed.js) scope the queue before the first
    // load. showQCed is a component toggle here (fetchQcBatch reads it from
    // its own commit), not a groupFilters key, so it is split out.
    const seed = routeFilterSeed(this.$route)
    if (seed.showQCed) {
      this.showQCed = true
      delete seed.showQCed
    }
    if (Object.keys(seed).length) {
      this.groupFilters = {...this.groupFilters, ...seed}
      this.$store.commit('setGroupFilter', this.groupFilters)
    }
    this.loadGroup()
    if (!this.allCategories.length) this.$store.dispatch('getCategoriesList')
    if (!this.allTags.length) this.$store.dispatch('getTagsList')
    this.$store.dispatch('getTagHierarchy')
  },

  watch: {
    // Facets differ per category, so a stale selection would wrongly hide tags
    // after a category switch.
    activeCategoryId() {
      this.selectedHierarchyIds = []
    },
  },

  mounted() {
    this.$refs.root && this.$refs.root.focus()
  },

  methods: {
    loadGroup() {
      const action = this.reviewMode ? 'fetchQcBatch' : 'fetchBoxGroup'
      const payload = this.reviewMode
        ? {onlyNeedsReview: this.onlyNeedsReview, showQCed: this.showQCed}
        : undefined
      return this.$store.dispatch(action, payload).catch(() => {
        this.notify(this.reviewMode ? 'Could not load the QC queue.' : 'Could not load the next group.')
      })
    },

    // --- box-QC actions (IT-745) -----------------------------------------
    approveGroup() {
      return this.$store.dispatch('approveQcGroup')
        .then(() => this.notify('Approved — next group.'))
        .catch(() => this.notify('Could not approve. Nothing was changed.'))
    },

    flagGroup() {
      return this.$store.dispatch('flagQcGroup')
        .then(() => this.notify('Flagged for re-review — next group.'))
        .catch(() => this.notify('Could not flag. Nothing was changed.'))
    },

    /** Re-fetch the QC batch whenever a queue toggle changes. [IT-745, IT-788] */
    reloadQcQueue() {
      return this.$store.dispatch('fetchQcBatch', {
        onlyNeedsReview: this.onlyNeedsReview,
        showQCed: this.showQCed,
      }).catch(() => this.notify('Could not load the QC queue.'))
    },

    /** Apply a new queue filter and re-fetch (persisted so getNext stays scoped). [IT-744] */
    setFilters(newFilter) {
      this.groupFilters = {...this.groupFilters, ...newFilter}
      this.$store.commit('setGroupFilter', this.groupFilters)
      return this.loadGroup()
    },

    /** Open the submit confirmation (no-op if the group isn't complete). */
    openSubmit(getNext) {
      if (!this.groupSubmittable) return
      this.submitDialog = {show: true, getNext}
    },

    confirmSubmit() {
      const getNext = this.submitDialog.getNext
      this.submitDialog.show = false
      return this.$store.dispatch('submitBoxGroup', {getNext})
        .then(() => this.notify(getNext ? 'Group submitted — loaded the next group.' : 'Group submitted.'))
        .catch(() => this.notify('Could not submit the group. Nothing was changed.'))
    },

    /**
     * Filmstrip thumbnails via the CDN's resize endpoint.
     *
     * These photos are 3-4 MB each at 3024x4032; rendering them as 84x64px
     * meant a 16-photo group pulled ~50 MB before a tagger could start. The CDN
     * serves a resized copy by inserting /width/<n>/ after the origin — 160px
     * is ~10 KB, roughly 300x smaller.
     */
    thumbUrl(url, width = 160) {
      if (!url) return url
      try {
        const parsed = new URL(url)
        // Only rewrite paths we haven't already rewritten.
        if (parsed.pathname.startsWith(`/width/`)) return url
        return `${parsed.origin}/width/${width}${parsed.pathname}${parsed.search}`
      } catch (e) {
        return url
      }
    },

    /** If the resize endpoint misses, fall back to the original rather than a broken tile. */
    onThumbError(event, photo) {
      if (event.target.src !== photo.image_url) event.target.src = photo.image_url
    },

    boxesForPhoto(photoId) {
      return this.allBoxes.filter(box => box.photo_id === photoId)
    },

    selectPhoto(photoId) {
      this.$store.commit('setActivePhoto', photoId)
    },

    onSetCategory(categoryId) {
      this.$store.commit('setActiveCategory', categoryId)
    },

    setViewMode(mode) {
      if (mode) this.$store.commit('setViewMode', mode)
    },

    toggleViewMode() {
      this.setViewMode(this.viewMode === 'focus' ? 'group' : 'focus')
    },

    setInteractionMode(mode) {
      if (mode) this.interactionMode = mode
    },

    toggleZoom() {
      this.interactionMode = this.interactionMode === 'zoom' ? 'select' : 'zoom'
    },

    onDrawBox(geom) {
      return this.$store.dispatch('createBox', geom)
        .then(() => this.notify('Box created.'))
        .catch(() => this.notify('Could not create the box.'))
    },

    onResizeBox(payload) {
      return this.$store.dispatch('updateBoxGeometry', {
        boxId: payload.id, x: payload.x, y: payload.y, width: payload.width, height: payload.height,
      }).catch(() => this.notify('Could not save the box size.'))
    },

    deleteSelected() {
      const ids = [...this.selectedBoxIds]
      if (!ids.length) return
      return Promise.all(ids.map(id => this.$store.dispatch('deleteBox', id)))
        .then(() => this.notify(`Deleted ${ids.length} box${ids.length === 1 ? '' : 'es'}.`))
        .catch(() => this.notify('Could not delete. Nothing was changed.'))
    },

    // --- selection -------------------------------------------------------

    onReplaceSelect(box) {
      this.$store.commit('setSelection', [box.id])
    },

    onToggleSelect(box) {
      this.$store.commit('toggleBoxSelection', box.id)
    },

    onMarqueeReplace(ids) {
      this.$store.commit('setSelection', ids)
    },
    onMarqueeAdd(ids) {
      this.$store.commit('addToSelection', ids)
    },
    onMarqueeSubtract(ids) {
      this.$store.commit('removeFromSelection', ids)
    },
    clearSelection() {
      this.$store.commit('clearSelection')
    },

    selectAll() {
      this.$store.commit('setSelection', this.selectableBoxes().map(b => b.id))
    },

    selectUntagged() {
      const ids = this.activeCategoryTagIds
      const isUntagged = b => ids == null ? !b.tagged : !(b.tag_ids || []).some(id => ids.includes(id))
      this.$store.commit('setSelection', this.selectableBoxes().filter(isUntagged).map(b => b.id))
    },

    invertSelection() {
      const all = this.selectableBoxes().map(b => b.id)
      this.$store.commit('setSelection', all.filter(id => !this.selectedBoxIds.includes(id)))
    },

    selectableBoxes() {
      const source = this.viewMode === 'focus' ? this.activePhotoBoxes : this.allBoxes
      return source.filter(box => box.status === 'active')
    },

    // --- tagging ---------------------------------------------------------

    /**
     * Cycle a tag's pending intent against where the selection currently
     * stands: not-everywhere -> add, everywhere -> remove, pending -> clear.
     */
    onCycleTag(tagId) {
      const current = this.pendingTags[tagId]
      if (current) {
        this.$store.commit('setPendingTag', {tagId, intent: null})
        return
      }
      const state = this.tagStateForSelection(tagId)
      if (state === 'all') {
        // Already on every selected box — clicking removes it (same as multi).
        this.$store.commit('setPendingTag', {tagId, intent: 'remove'})
        return
      }
      // IT-749: a single-select category (multiple_select === false) holds one tag
      // per box. Before staging this add, clear any sibling in the same category:
      // drop a pending sibling-add, and mark an already-applied sibling for
      // removal — so the pending diff is a clean swap and the Apply never stages
      // two tags from one single-select category (which the server would reject).
      const tag = this.tagById[tagId]
      const category = tag && tag.category
      if (category && category.multiple_select === false) {
        this.allTags
          .filter(t => t.category && t.category.id === category.id && t.id !== tagId)
          .forEach(sibling => {
            const pending = this.pendingTags[sibling.id]
            if (pending === 'add') {
              this.$store.commit('setPendingTag', {tagId: sibling.id, intent: null})
            } else if (this.tagStateForSelection(sibling.id) !== 'none') {
              this.$store.commit('setPendingTag', {tagId: sibling.id, intent: 'remove'})
            }
          })
      }
      this.$store.commit('setPendingTag', {tagId, intent: 'add'})
    },

    applyTags() {
      if (!this.selectedBoxIds.length) return
      if (!this.pendingAddIds.length && !this.pendingRemoveIds.length) return

      const count = this.selectedBoxIds.length
      // In QC the write is attributed to the reviewer (isQc) so the original
      // tagger is preserved and a BoundingBoxCorrection is recorded. [IT-745]
      return this.$store.dispatch('applyBoxTags', {isQc: this.reviewMode})
        .then(() => this.notify(`Updated ${count} box${count === 1 ? '' : 'es'}.`))
        .catch(() => this.notify('Could not save. Nothing was changed.'))
    },

    reject(reason) {
      if (!this.selectedBoxIds.length) return
      const count = this.selectedBoxIds.length
      const dup = reason === 'duplicate'
      return this.$store.dispatch('applyBoxTags', {
        ...(dup ? {markDuplicate: true} : {markInvalid: true}),
        isQc: this.reviewMode,
      })
        .then(() => this.notify(`Marked ${count} box${count === 1 ? '' : 'es'} ${dup ? 'duplicate' : 'invalid'}.`))
        .catch(() => this.notify('Could not save. Nothing was changed.'))
    },

    // --- keyboard --------------------------------------------------------

    onKeydown(event) {
      // Never hijack typing in the filter box or any other input.
      const tag = (event.target.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || event.target.isContentEditable) {
        if (event.key === 'Escape') event.target.blur()
        return
      }
      if (event.metaKey || event.ctrlKey) return

      const key = event.key

      switch (key) {
        case 'Enter':
          this.applyTags(); event.preventDefault(); break
        case 'Escape':
          this.clearSelection(); break
        case 'Backspace':
        case 'Delete':
          if (this.interactionMode === 'draw') this.deleteSelected()
          else this.reject(this.rejectReason)
          event.preventDefault(); break
        case 'a': case 'A':
          this.selectAll(); event.preventDefault(); break
        case 'u': case 'U':
          this.selectUntagged(); event.preventDefault(); break
        case 'i': case 'I':
          this.invertSelection(); event.preventDefault(); break
        case 'g': case 'G':
          this.toggleViewMode(); event.preventDefault(); break
        case 'z': case 'Z':
          this.toggleZoom(); event.preventDefault(); break
        case 'd': case 'D':
          this.interactionMode = this.interactionMode === 'draw' ? 'select' : 'draw'; event.preventDefault(); break
        case 'ArrowRight':
          this.stepPhoto(1); event.preventDefault(); break
        case 'ArrowLeft':
          this.stepPhoto(-1); event.preventDefault(); break
        case '/':
          this.$refs.tagPanel && this.$refs.tagPanel.focusSearch(); event.preventDefault(); break
        case '?':
          this.showHelp = !this.showHelp; event.preventDefault(); break
      }
    },

    stepPhoto(delta) {
      if (!this.group || this.viewMode !== 'focus') return
      const photos = this.group.photos
      const index = photos.findIndex(p => p.id === this.activePhotoId)
      const next = photos[index + delta]
      if (next) this.selectPhoto(next.id)
    },

    notify(text) {
      this.snackbar = {show: true, text}
    },
  },
}
</script>

<style scoped>
.box-tagging {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  outline: none;
  padding: 0 16px 12px;
}

.box-tagging__filter {
  flex: 0 0 auto;
  padding-top: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.box-tagging__needs-review {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}

.box-tagging__bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  flex: 0 0 auto;
}

.box-tagging__ident {
  font-size: 15px;
  font-weight: 500;
}

.box-tagging__stats {
  display: flex;
  gap: 14px;
  font-size: 13px;
}

.stat strong {
  font-variant-numeric: tabular-nums;
}

.stat--done {
  color: var(--v-success-base);
  font-weight: 600;
}

.box-tagging__controls {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.submit-hint {
  font-size: 12px;
  font-weight: 600;
  color: #c62828;
  white-space: nowrap;
}

.box-tagging__loading,
.box-tagging__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.box-tagging__filmstrip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  flex: 0 0 auto;
}

.thumb {
  position: relative;
  flex: 0 0 auto;
  width: 84px;
  height: 64px;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  background: #000;
  cursor: pointer;
  padding: 0;
}

.thumb.is-active {
  border-color: #1e88e5;
}

.thumb__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.85;
}

.thumb__index {
  position: absolute;
  left: 3px;
  top: 2px;
  font-size: 10px;
  color: #fff;
  text-shadow: 0 0 3px #000;
}

.thumb__badge {
  position: absolute;
  right: 3px;
  bottom: 3px;
  min-width: 17px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #fb8c00;
  border-radius: 9px;
  padding: 1px 5px;
}

.thumb__done {
  position: absolute;
  right: 3px;
  bottom: 3px;
  color: var(--v-success-base) !important;
}

.box-tagging__body {
  flex: 1 1 auto;
  display: flex;
  gap: 16px;
  min-height: 0;
}

.box-tagging__stage {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.box-tagging__grid {
  /* Portrait shelf photos are taller than wide, so the row is sized by height
     and each photo takes only the width its aspect ratio needs. Sizing by a
     width percentage instead left a dead band under every photo and pushed the
     rest off-screen, turning a glance into a scroll. */
  flex: 1 1 auto;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  align-items: stretch;
  min-height: 0;
}

.box-tagging__grid-item {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* The canvas measures this box, so it must be the height budget and nothing more. */
.box-tagging__grid-item > .box-canvas-host {
  flex: 1 1 auto;
  min-height: 0;
}

.box-tagging__grid-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 2px;
}

.box-tagging__legend {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 8px;
  font-size: 12px;
  flex: 0 0 auto;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: rgba(0, 0, 0, 0.7);
}

.swatch {
  width: 14px;
  height: 0;
  border-top-width: 2px;
  border-top-style: solid;
  display: inline-block;
}

/* Mirrors the 2x2 in BoxCanvas.vue — keep the two in step. */
.swatch--untagged          { border-top-color: #fdd835; border-top-style: dashed; }
.swatch--untagged-selected { border-top-color: #fb8c00; border-top-width: 3px; }
.swatch--tagged            { border-top-color: #1e88e5; }
.swatch--tagged-selected   { border-top-color: #8e24aa; border-top-width: 3px; }
.swatch--invalid           { border-top-color: #e53935; border-top-style: dashed; }

.legend-toggle {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

.legend-toggle >>> label {
  font-size: 12px;
}

.box-tagging__side {
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.shortcut {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 3px 0;
  font-size: 13px;
}

.shortcut kbd {
  flex: 0 0 76px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  /* Was near-invisible: light grey on a light grey chip. Give the key itself
     real contrast and a border so it reads as a key. */
  color: rgba(0, 0, 0, 0.87);
  background: #f2f2f4;
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 3px 6px;
  text-align: center;
}

.shortcut span {
  color: rgba(0, 0, 0, 0.82);
}
.swatch--duplicate         { border-top-color: #ff7043; border-top-style: dotted; }

.show-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.legend-show {
  margin-left: 4px;
}

/* Dark mode (restyle step 5): flip the rgba(0,0,0,..) label/legend greys and
   the keycap chip to light-on-dark. The bbox swatch colors (yellow/orange/
   blue/purple/red) are a saturated shared state encoding and read on dark, so
   they're left alone. .theme--dark is the whole-app dark class on the v-app
   ancestor. */
.theme--dark .box-tagging__grid-label { color: rgba(255, 255, 255, 0.6); }
.theme--dark .legend-item { color: rgba(255, 255, 255, 0.75); }
.theme--dark .shortcut kbd {
  color: rgba(255, 255, 255, 0.9);
  background: #2a2a2e;
  border-color: rgba(255, 255, 255, 0.28);
}
.theme--dark .shortcut span { color: rgba(255, 255, 255, 0.82); }
.theme--dark .show-label { color: rgba(255, 255, 255, 0.6); }
.theme--dark .submit-hint { color: #ff8a80; }
</style>
